// Metadata-only device editor (B.2.2). Edits display_label / location_note /
// notes; config + area stay CSV-only. PATCH /devices/{slug}.

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { devices as devicesApi } from "../../api/endpoints/devices";
import { qk } from "../../api/queryKeys";
import { FormShell } from "../../components/forms/FormShell";
import { TextField } from "../../components/forms/TextField";
import { Modal } from "../../components/ui/Modal";
import type { DeviceOut } from "../../api/types/device";

interface Props {
  accountSlug: string;
  tenantSlug: string;
  siteSlug: string;
  device: DeviceOut | null;
  onClose: () => void;
}

export function DeviceEditModal({
  accountSlug,
  tenantSlug,
  siteSlug,
  device,
  onClose,
}: Props) {
  const queryClient = useQueryClient();
  const [displayLabel, setDisplayLabel] = useState("");
  const [locationNote, setLocationNote] = useState("");
  const [notes, setNotes] = useState("");

  // Seed the form whenever a new device is opened.
  useEffect(() => {
    if (device) {
      setDisplayLabel(device.display_label);
      setLocationNote(device.location_note ?? "");
      setNotes(device.notes ?? "");
    }
  }, [device]);

  const mutation = useMutation({
    mutationFn: () =>
      devicesApi.patch(accountSlug, tenantSlug, siteSlug, device!.slug, {
        display_label: displayLabel,
        location_note: locationNote || null,
        notes: notes || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.devices(accountSlug, tenantSlug, siteSlug),
      });
      onClose();
    },
  });

  const isValid = displayLabel.trim() !== "";

  return (
    <Modal
      open={device !== null}
      onClose={onClose}
      closeDisabled={mutation.isPending}
      title={device ? `Edit ${device.slug}` : "Edit device"}
      width={480}
    >
      <FormShell
        submitLabel="Save"
        onSubmit={(e) => {
          e.preventDefault();
          if (isValid) mutation.mutate();
        }}
        onCancel={onClose}
        pending={mutation.isPending}
        error={mutation.error}
        errorTitle="Save failed"
      >
        <TextField
          label="Display label"
          value={displayLabel}
          onChange={setDisplayLabel}
          required
          helper="Config (tuya keys, relay count) and area are edited via CSV re-import, not here."
        />
        <TextField
          label="Location note"
          value={locationNote}
          onChange={setLocationNote}
          placeholder="north wall, behind sofa"
        />
        <TextField
          label="Notes"
          value={notes}
          onChange={setNotes}
        />
      </FormShell>
    </Modal>
  );
}
